import { useEffect, useRef } from 'react';
import { useRechargeModal } from 'modules/User/components/Settings/Billing/hooks/useRechargeModal';
import { useAutoRechargeModal } from 'modules/User/components/Settings/Billing/hooks/useAutoRechargeModal';
import { useQueryClient } from 'react-query';
import { queryKeys } from 'utils/react-query';
import { addNotification, removeNotification } from './notificationStore';
import { useNotificationApi } from './useNotificationApi';

/**
 * Hook that provides notification management capabilities
 * Uses the useNotificationApi hook to interact with backend notifications
 * and automatically displays them using the addNotification function
 */

const NOTIFICATION_HEADER = {
  CreditsFail: 'Credits auto top-up failed',
  CreditsSuccess: 'Credits auto top-up successful',
  NewNotification: 'You have a new notification',
};

const NOTIFICATION_TYPE: Record<string, 'error' | 'success' | 'info' | 'warning'> = {
  [NOTIFICATION_HEADER.CreditsFail]: 'error',
  [NOTIFICATION_HEADER.CreditsSuccess]: 'success',
  [NOTIFICATION_HEADER.NewNotification]: 'info',
};

const getActionButtonsConfig = (
  notification: CF.API.Notifications.NotificationEntity,
  markAsRead: (ids: string[]) => void,
  openRechargeModal: () => void,
  openAutoRechargeModal: () => void,
) => {
  switch (notification.title) {
    case NOTIFICATION_HEADER.CreditsFail:
      return {
        acceptBtn: {
          text: 'Recharge',
          onClick: () => {
            markAsRead([notification.id]);
            openRechargeModal();
          },
          disabled: false,
        },
        declineBtn: {
          text: 'Set auto recharge',
          onClick: () => {
            markAsRead([notification.id]);
            openAutoRechargeModal();
          },
          disabled: false,
        },
      };
    default:
      return {
        acceptBtn: {
          text: 'Mark as Read',
          onClick: () => {
            markAsRead([notification.id]);
          },
          disabled: false,
        },
        declineBtn: null,
      };
  }
};

export const useNotification = () => {
  const { notifications, isLoading, markAsRead, isMarkingAsRead } = useNotificationApi();
  const openAutoRechargeModal = useAutoRechargeModal();
  const openRechargeModal = useRechargeModal();
  const queryClient = useQueryClient();

  const displayedNotificationsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!notifications || isLoading) return;
    const backendNotificationIds = new Set(notifications.map((n) => n.id));

    const displayedIds = Array.from(displayedNotificationsRef.current);
    displayedIds.forEach((id) => {
      if (!backendNotificationIds.has(id)) {
        removeNotification(id);
        displayedNotificationsRef.current.delete(id);
      }
    });

    // Remove notifications from store that are now marked as read
    notifications.forEach((backendNotification) => {
      if (backendNotification.is_read && displayedNotificationsRef.current.has(backendNotification.id)) {
        removeNotification(backendNotification.id);
        displayedNotificationsRef.current.delete(backendNotification.id);
      }
    });

    const newUnreadNotifications = notifications
      .filter((notification) => !notification.is_read && !displayedNotificationsRef.current.has(notification.id))
      ?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    newUnreadNotifications.forEach((backendNotification) => {
      displayedNotificationsRef.current.add(backendNotification.id);

      // Invalidate credit balance queries when credit top-up is successful
      if (backendNotification.title === NOTIFICATION_HEADER.CreditsSuccess) {
        queryClient.invalidateQueries([queryKeys.userCreditBalance]);
      }

      addNotification({
        id: backendNotification.id,
        type: NOTIFICATION_TYPE[backendNotification.title],
        title: backendNotification.title || 'Notification',
        header: backendNotification.title || 'New Notification',
        body: backendNotification.summary || 'You have a new notification',
        hideIcon: true,
        isLoading: false,
        createdDate: backendNotification.created_at,
        ...getActionButtonsConfig(backendNotification, markAsRead, openRechargeModal, openAutoRechargeModal),
      });
    });
  }, [notifications, markAsRead, isLoading, isMarkingAsRead, openRechargeModal, openAutoRechargeModal, queryClient]);
};
